import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styles from './StudentGraduationSummary.module.scss';
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import { useToasts } from "../../../contexts/ToastContext";
import { useAuth } from "../../../contexts/AuthContext";
import axiosClient from "../../../api/axiosClient";
import GraduationProfile from "../../../components/graduationProfile/GraduationProfile";
import GraduationTimeline from "../../../components/graduationTimeline/GraduationTimeline";
import CategoryPerformance from "../../../components/categoryPerformance/CategoryPerformance";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import Button from "../../../components/button/Button";
import InformationCard from "../../../components/informationCard/InformationCard";
import CumulativeClinicalDaySummary from "../../../components/cumulativeClinicalDaySummary/CumulativeClinicalDaySummary";
import StudentTrendChart from "../../../components/studentTrendChart/StudentTrendChart";

const StudentGraduationSummary = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { role, can } = useAuth();
  const canUpdateStudentProfile = role !== 'Program Coordinator' || can('canUpdateStudentProfile');
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState({
    overview: {},
    timeline: [],
    competencyBasePerformance: [],
    specialityBasePerformance: [],
    instructorBasePerformance: [],
    clinicalSiteBasePerformance: [],
    personalInformation: {},
    assignmentDetails: {},
  });
  const { showToast } = useToasts();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await axiosClient.get(`evaluations/students/${studentId}/graduation-summary`);
        setData(response.data);
      } catch (error) {
        showToast(error.message, 'error');
      } finally {
        setIsLoading(false);
      }
    };

    if (studentId) {
      fetchData();
    }
  }, [studentId, showToast]);

  const competencyData = data?.competencyBasePerformance?.map((item, index) => ({
    name: item.tag,
    cumulativeScore: item.cumulativeScore,
    variant: (index % 6) + 1,
  })) || [];

  const specialityData = data?.specialityBasePerformance?.map(item => ({
    name: item.speciality.name,
    cumulativeScore: item.cumulativeScore,
  })) || [];

  const instructorData = data?.instructorBasePerformance?.map(item => ({
    name: `${item.instructor.firstName} ${item.instructor.lastName}`,
    firstName: item.instructor.firstName,
    lastName: item.instructor.lastName,
    profilePicture: item.instructor.profilePicture,
    cumulativeScore: item.cumulativeScore,
  })) || [];

  const clinicalSiteData = data?.clinicalSiteBasePerformance?.map(item => ({
    name: item.clinicalSite.name,
    cumulativeScore: item.cumulativeScore,
  })) || [];

  const cumulativeViews = [
    { label: 'Competency', value: 'competency', data: competencyData, type: 'tag', itemsPerRow: 4 },
    { label: 'Specialty', value: 'specialty', data: specialityData, type: 'name', itemsPerRow: 5 },
    { label: 'Instructor', value: 'instructor', data: instructorData, type: 'instructor', itemsPerRow: 4 },
    { label: 'Clinical Site', value: 'clinicalSite', data: clinicalSiteData, type: 'name', itemsPerRow: 5 },
  ];

  // Map timeline data for GraduationTimeline
  const timelineData = data?.timeline?.map((item) => {
    const statusMap = {
      'COMPLETED': 'Completed',
      'IN_PROGRESS': 'In Progress',
      'NOT_STARTED': 'Not Started',
    };
    const classMap = {
      'COMPLETED': 'completed',
      'IN_PROGRESS': 'inProgress',
      'NOT_STARTED': 'notStarted',
    };

    return {
      year: item.year.year,
      status: statusMap[item.status] || 'Not Started',
      statusClass: classMap[item.status] || 'notStarted',
      cumulativeScore: item.cumulativeScore || '0',
      scorePercentage: item.cumulativeScore ? Math.round(item.cumulativeScore * 20) : 0,
      scoreColor: item.status === 'COMPLETED' ? 'green' : item.status === 'IN_PROGRESS' ? 'red' : 'grey',
    };
  }) || [];

  const { overview } = data;

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.breadcrumbHeader}>
        <div className={styles.breadcrumbs}>
          <Breadcrumbs
            breadcrumbs={[
              { label: 'Student Management', path: '/students' },
              { label: data?.overview?.student?.firstName && data?.overview?.student?.lastName ? `${data?.overview?.student?.firstName} ${data?.overview?.student?.lastName}` : 'Student Graduation Summary' }
            ]}
          />
        </div>
        {canUpdateStudentProfile && (
          <Button
            label="Edit Student Details"
            onClick={() => navigate(`/students/edit/${studentId}`)}
            size="medium"
          />
        )}
      </div>
      <div className={styles.container}>
        <GraduationProfile overview={overview} programDetails={data?.programDetails} />
        <GraduationTimeline data={timelineData} minimumScore={overview?.minimumScore} />
        <CategoryPerformance
          views={cumulativeViews}
          title="Cumulative Score Summary"
        />
        {data?.personalInformation && data?.assignmentDetails && (
          <InformationCard
            title="Personal & Clinical Assignment Information"
            items={[
              { label: 'Email', value: data?.personalInformation?.email },
              { label: 'Phone', value: data?.personalInformation?.mobileNumber },
              { label: 'Date of Birth', value: data?.personalInformation?.dateOfBirth },
              {
                label: 'Specialty',
                value: data?.assignmentDetails?.specialities && data.assignmentDetails.specialities.length > 0
                  ? data.assignmentDetails.specialities.map(s => s.name).join(', ')
                  : undefined
              },
              {
                label: 'Instructor',
                value: data?.assignmentDetails?.instructor
                  ? `${data.assignmentDetails.instructor.firstName} ${data.assignmentDetails.instructor.lastName}`
                  : undefined
              },
            ]}
            itemsPerRow={3}
          />
        )}
        <CumulativeClinicalDaySummary studentId={studentId} />
        <StudentTrendChart userId={studentId} />
      </div>
    </>
  );
};

export default StudentGraduationSummary;
